import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {BotInListDTO, BotTableRequest, BotTableResponse} from "../../../api/generated";
import {botApi, RequestStatus} from "../../../api/apiConfiguration";
import {RootState} from "../../../app/store";

export const BOT_TABLE_ITEMS_ON_PAGE = 10

export type BotTableRequestUi = BotTableRequest & {
    franchisePublicIds?: string[];
}

export type BotTableRequestPartial = Partial<BotTableRequestUi>

export const defaultBotTableRequest: BotTableRequestUi = {
    searchQuery: "",
    botTypes: [],
    franchisePublicIds: [],
    pageNumber: 0,
    pageSize: BOT_TABLE_ITEMS_ON_PAGE
}

export interface BotState {
    totalCount: number
    botList: BotInListDTO[]
    botListRequestStatus: RequestStatus
    botTableRequest: BotTableRequestUi
}

const buildInitialState = (): BotState => {
    return {
        totalCount: 0,
        botList: [],
        botListRequestStatus: RequestStatus.NONE,
        botTableRequest: defaultBotTableRequest
    }
}

const initialState: BotState = buildInitialState()

export const fetchBotList = createAsyncThunk<BotTableResponse, undefined, { state: RootState }>(
    "botList/getBotList",
    (_payload, {getState}) => {
        const botTableRequest = getState().botList.botTableRequest;
        return botApi.getBotsTable({
            botTableRequest: botTableRequest as BotTableRequest
        });
    }
);

export const deleteBot = createAsyncThunk<string, string>(
    "botList/deleteBot",
    (publicId) => {
        return botApi.deleteByPublicBotId({publicId}).then(r => publicId);
    }
);

export const botListSlice = createSlice({
    name: 'botList',
    initialState,
    reducers: {
        clearBotListState: () => {
            return buildInitialState();
        },
        updateBotTableRequest: (state, {payload}: PayloadAction<BotTableRequestPartial>) => {
            if (!payload.pageNumber) {
                payload.pageNumber = 0
            }
            state.botTableRequest = {
                ...state.botTableRequest,
                ...payload
            }
        },
        resetBotTableRequest: (state) => {
            state.botTableRequest = {
                ...defaultBotTableRequest,
                sortBy: state.botTableRequest.sortBy,
                sortDirection: state.botTableRequest.sortDirection
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchBotList.pending, (state) => {
            state.botListRequestStatus = RequestStatus.PENDING;
        })
        builder.addCase(fetchBotList.fulfilled, (state, {payload}) => {
            state.botList = payload.bots;
            state.totalCount = payload.totalCount;
            state.botListRequestStatus = RequestStatus.RESOLVED;
        })
        builder.addCase(fetchBotList.rejected, (state) => {
            state.botListRequestStatus = RequestStatus.REJECTED;
        })

        builder.addCase(deleteBot.fulfilled, (state, {payload}) => {
            const publicId = payload
            const index = state.botList.findIndex(bot => bot.publicId === publicId)
            state.botList.splice(index, 1)
            state.totalCount = state.totalCount - 1
        })
    },
});

export const {
    clearBotListState,
    updateBotTableRequest,
    resetBotTableRequest
} = botListSlice.actions;

export const botListSelector = (state: RootState): BotInListDTO[] => state.botList.botList;
export const botListTotalCountSelector = (state: RootState): number => state.botList.totalCount;
export const botListRequestStatusSelector = (state: RootState): RequestStatus => state.botList.botListRequestStatus;
export const botTableRequestSelector = (state: RootState): BotTableRequestUi => state.botList.botTableRequest;
